import { useEffect, useRef, memo } from 'react';
import { useSession } from 'next-auth/react';
import useCallStore from '../store/useCallStore';
import CallModal from './CallModal';
import { useConsultationWatcher } from 'hooks/useConsultationWatcher';
import axiosInstance from 'helper/apis/axiosInstance';

const CallModalProvider = ({ children }) => {
    const { data: session } = useSession();
    const { status: watcherStatus } = useConsultationWatcher();
    const cleanupTimeoutRef = useRef(null);

    const {
        isInitialized,
        initializeCall,
        cleanup,
        isOpen,
        callStatus,
        setPeerUser,
        registerPeer,
        unregisterPeer,
    } = useCallStore();

    // Effect to set the user in the store once the session is available
    useEffect(() => {
        if (session?.user) {
            setPeerUser({
                userId: session.user.id,
                userType: session.user.role,
                userName: session.user.full_name,
                avatar: session.user.avatar,
            });
        }
    }, [session?.user, setPeerUser]);

    // Effect to manage the user's online status
    useEffect(() => {
        const userId = session?.user?.id;
        if (!userId) return;

        // Set user to offline when the provider unmounts
        return () => {
            unregisterPeer();
        };
    }, [session?.user?.id, unregisterPeer]);

    // Effect to manage the PeerJS connection based on the watcher status
    useEffect(() => {
        // If we are in the active window for a call, initialize the connection
        if (watcherStatus === 'ACTIVE_WINDOW') {
            // Cancel any pending cleanup
            if (cleanupTimeoutRef.current) {
                clearTimeout(cleanupTimeoutRef.current);
                cleanupTimeoutRef.current = null;
            }
            // Initialize if not already
            if (!isInitialized) {
                initializeCall();
            }
        }
        // If there are no upcoming calls or we are idle, schedule a cleanup
        else if (watcherStatus === 'NO_UPCOMING_CALLS' || watcherStatus === 'IDLE') {
            if (isInitialized && callStatus === 'disconnected') {
                // Avoid scheduling multiple cleanups
                if (cleanupTimeoutRef.current) clearTimeout(cleanupTimeoutRef.current);
                cleanupTimeoutRef.current = setTimeout(() => {
                    cleanup();
                }, 10000); // 10-second delay before cleaning up
            }
        }

        return () => {
            if (cleanupTimeoutRef.current) {
                clearTimeout(cleanupTimeoutRef.current);
            }
        };
    }, [watcherStatus, isInitialized, callStatus, initializeCall, cleanup]);

    return (
        <>
            {children}
            {isOpen && <CallModal />}
        </>
    );
};

export default memo(CallModalProvider);
